/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ModuleViewMoreDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import CustomDialogBoxV2 from "../../../common/customDialogBox/CustomDialogBoxV2";
import { Box, Button, Select, MenuItem } from "@mui/material";
import CustomTabsV2 from "../../../common/tabs/CustomTabsV2";
import ModuleViewMore from "./ModuleViewMore";
import ViewUploadedFile from "../../../../file-upload/ViewUploadedFile";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, labCar } from "../../../../../redux/actions";
import ComponentFunction from "./ComponentFunction";
import CustomToolTip from "../../../../infoButton/CustomToolTip";
import CustomSearchFilterV2 from "../../../common/searchBar/CustomSearchFilterV2";
import CustomPaginationV2 from "../../../common/pagination/CustomPaginationV2";

function ModuleViewMoreDialog({
  isEEDialogOpen,
  toggleEEDialog,
  selectedComponent,
  linkApiProps,
  apiAction,
  apiDataKey,
  type,
  inline = false,
  projectId,
  variantId,
  showSupplier = true,
}) {
  const dispatch = useDispatch();
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedVariant = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails
  );
  const resolvedProjectId = projectId ?? selectedProject?.project_id;
  const resolvedVariantId = variantId ?? selectedVariant?.variant_id;

  const [localLinkApiProps, setLocalLinkApiProps] = useState(linkApiProps);
  const [linkedComponent, setLinkedComponent] = useState([]);
  const [componentSelfData, setComponentSelfData] = useState({});
  const [componentFunction, setComponentFunction] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [filteredAttributes, setFilteredAttributes] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const restrictedTab = useMemo(
    () => ({
      EE: [],
      SWM: [],
      VS: [0],
      VC: [0],
    }),
    []
  );

  const getDefaultTab = useCallback(() => {
    for (let i = 0; i < 3; i++) {
      let restrictedArr = restrictedTab[type] || [];
      if (!restrictedArr.includes(i)) {
        return i;
      }
    }
    return 0;
  }, [restrictedTab, type]);

  const [viewMoreTabValue, setViewMoreTabValue] = useState(getDefaultTab());

  useEffect(() => {
    setViewMoreTabValue(getDefaultTab());
  }, [type, getDefaultTab]);

  useEffect(() => {
    setLocalLinkApiProps(linkApiProps);
  }, [linkApiProps]);

  const getComponentApiByType = useCallback(() => {
    console.log("---getComponentApiByType---", type);

    const activeEE = Array.isArray(selectedComponent?.ee_details)
      ? selectedComponent.ee_details.find(
          (ee) => ee.ee_id === localLinkApiProps?.ee_id
        ) || selectedComponent.ee_details[0]
      : selectedComponent?.ee_details;

    const activeSWM = Array.isArray(selectedComponent?.swm_details)
      ? selectedComponent.swm_details.find(
          (swm) => swm.swm_id === localLinkApiProps?.swm_id
        ) || selectedComponent.swm_details[0]
      : selectedComponent?.swm_details;

    switch (type) {
      case "EE":
        return {
          apiAction: labCar?.get_ee_components,
          tabName: "Software Modules",
          idKey: "ee_id",
          linkedComponentsForTickets:
            activeEE?.linked_ee_ids?.map(Number) || [],
          linkedComponentsFilterEnabled: true,

          headder: {
            name: componentSelfData.assembly_number || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
            supplier: componentSelfData.supplier_name || "-",
          },

          childApiAction: labCar?.get_sw_modules,
          childKey: "description",
          searchKeys: ["name", "version", "description"],
        };
      case "SWM":
        return {
          apiAction: labCar?.get_sw_modules,
          componentFunctionKey: "vehicle_functions",
          tabName: "Vehicle Functions",
          idKey: "swm_id",
          linkedComponentsForTickets:
            activeSWM?.linked_swm_ids?.map(Number) || [],
          linkedComponentsFilterEnabled: true,

          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.version || "-",
            description: componentSelfData.description || "-",
            supplier: componentSelfData.supplier_name || "-",
          },

          childApiAction: labCar?.get_sw_modules,
          childKey: "vehicle_functions",
          searchKeys: ["name", "abbreviation"],
        };

      case "VS":
        return {
          apiAction: labCar?.get_vehicle_systems,
          tabName: "Vehicle Components",
          idKey: "id",
          linkedComponentsForTickets: [],
          linkedComponentsFilterEnabled: false,

          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.assembly_number || "-",
            description: componentSelfData.description || "-",
            supplier: selectedComponent?.supplier_name || "-",
          },

          childApiAction: labCar?.get_vehicle_components,
          childKey: "description",
          searchKeys: ["name", "component_number", "description"],
        };

      case "VC":
        return {
          apiAction: labCar?.get_vehicle_components,
          tabName: "EE Components",
          idKey: "id",
          linkedComponentsForTickets: [],
          linkedComponentsFilterEnabled: false,

          headder: {
            name: componentSelfData.name || "-",
            version: componentSelfData.component_number || "-",
            description: componentSelfData.description || "-",
            supplier: selectedComponent?.supplier_name || "-",
          },
          childApiAction: labCar?.get_ee_components,
          childKey: "description",
          searchKeys: [
            "assembly_number",
            "description",
            "supplier_name",
            "version",
          ],
        };
      default:
        return {};
    }
  }, [type, selectedComponent, localLinkApiProps, componentSelfData]);

  // Use a ref so getComponentDetails / getLinkedComponents always call the
  // *latest* version of getComponentApiByType without adding it to their own
  // dependency arrays. This prevents the cycle:
  //   getComponentDetails runs → sets componentSelfData →
  //   getComponentApiByType changes → getComponentDetails changes →
  //   useEffect re-fires → infinite API calls
  const getComponentApiByTypeRef = useRef(getComponentApiByType);
  useEffect(() => {
    getComponentApiByTypeRef.current = getComponentApiByType;
  }, [getComponentApiByType]);

  const getComponentDetails = useCallback(async () => {
    try {
      const componentApiByType = getComponentApiByTypeRef.current();
      console.log(
        "---getComponentDetails--",
        componentApiByType?.apiAction
      );
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...componentApiByType?.apiAction,
            params: {
              ...localLinkApiProps,
              project_id: resolvedProjectId,
              variant_id: resolvedVariantId,
              page_number: page,
              page_size: rowsPerPage,
            },
            show_toast: false,
          })
        )
      );

      if (resp?.error) {
        setComponentSelfData({});
        setAttributes([]);
        setFilteredAttributes([]);
        setPage(1);
      } else {
        console.log("resp?.data?.[0]", resp?.data?.[0]);
        setComponentSelfData(resp?.data?.[0]);

        const attrs = resp?.data?.[0]?.attributes || [];
        setAttributes(attrs);
        setFilteredAttributes(attrs);
        setPage(1);
      }

      let childResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...componentApiByType?.childApiAction,
            params: {
              ...localLinkApiProps,
              project_id: resolvedProjectId,
              variant_id: resolvedVariantId,
              page_number: page,
              page_size: rowsPerPage,
            },
            show_toast: false,
          })
        )
      );

      console.log("---childResp---", childResp);

      if (childResp?.error) {
        setComponentFunction([]);
      } else if (type === "SWM") {
        setComponentFunction(
          childResp?.data?.[0]?.[componentApiByType?.childKey] || []
        );
      } else {
        setComponentFunction(childResp?.data);
      }
    } catch (error) {
      console.error("Failed to fetch component details:", error);
      setComponentSelfData({});
      setComponentFunction([]);
      setAttributes([]);
      setFilteredAttributes([]);
      setPage(1);
    }
  }, [
    dispatch,
    localLinkApiProps,
    resolvedProjectId,
    resolvedVariantId,
    page,
    rowsPerPage,
    type,
  ]);

  const getVersionLabel = () => {
    if (type === "VS") return "Assembly Number:";
    if (type === "VC") return "Component Number:";
    return "Version:";
  };

  const getLinkedComponents = useCallback(async () => {
    try {
      const apiDetails = apiAction;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              ...localLinkApiProps,
              project_id: resolvedProjectId,
              variant_id: resolvedVariantId,
            },
            show_toast: false,
          })
        )
      );

      const {
        linkedComponentsFilterEnabled,
        linkedComponentsForTickets,
        idKey,
      } = getComponentApiByTypeRef.current();
      const responseData = resp?.data?.[0] || {};
      const linkedIdsKey = apiDataKey?.replace("_details", "_ids");
      const freshLinkedComponentIds =
        responseData?.[linkedIdsKey]?.map(Number) || [];
      const linkedComponentsToFilterBy = freshLinkedComponentIds.length
        ? freshLinkedComponentIds
        : linkedComponentsForTickets;

      console.log(
        "---linkedComponent response---",
        linkedComponentsToFilterBy,
        responseData?.[apiDataKey]
      );

      if (resp?.error) {
        setLinkedComponent([]);
      } else {
        const allLinked = responseData?.[apiDataKey] || [];

        const filtered = linkedComponentsFilterEnabled
          ? allLinked.filter((element) =>
              linkedComponentsToFilterBy.includes(Number(element[idKey]))
            )
          : allLinked;

        console.log("---filtered linkedComponent---", filtered);

        setLinkedComponent(
          filtered.map((item) => ({
            ...item,
            network: item.network_interface,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch linked components:", error);
      setLinkedComponent([]);
    }
  }, [
    dispatch,
    apiAction,
    localLinkApiProps,
    resolvedProjectId,
    resolvedVariantId,
    apiDataKey,
  ]);

  useEffect(() => {
    if (!inline && !isEEDialogOpen) {
      return;
    }

    getComponentDetails();
    getLinkedComponents();
  }, [
    inline,
    isEEDialogOpen,
    getComponentDetails,
    getLinkedComponents,
  ]);

  useEffect(() => {
    if (!inline && !isEEDialogOpen) {
      const actionType = labCar?.get_ee_components?.action_type;
      console.log("----cleanup redux actionType: ", actionType);
      if (actionType) {
        dispatch({
          type: actionType,
          payload: {
            data: [],
          },
        });
      }
    }
  }, [inline, isEEDialogOpen, dispatch]);

  const renderContent = () => (
    <Box sx={{ height: inline ? "100%" : "450px" }}>
      {console.log(
        "---View more linked component---",
        linkedComponent,
        getComponentApiByType()?.linkedComponentsForTickets
      )}
      <CustomTabsV2
        displayZero={true}
        value={viewMoreTabValue}
        defaultTab={getDefaultTab()}
        onTabChange={(newTab) => {
          if (!restrictedTab[type].includes(newTab)) {
            setViewMoreTabValue(newTab);
          }
        }}
        tabs={[
          {
            name: "Linked Component",
            count: linkedComponent?.length || 0,
            content: (
              <ModuleViewMore
                linkedComponent={linkedComponent}
                linkedComponentsForTickets={
                  getComponentApiByType()?.linkedComponentsForTickets
                }
                setLinkedComponent={setLinkedComponent}
                type={type}
              />
            ),
          },
          {
            name: getComponentApiByType()?.tabName,
            count: componentFunction?.length || 0,
            content: (
              <ComponentFunction
                type={type}
                componentFunction={componentFunction}
                searchKeys={getComponentApiByType()?.searchKeys}
                parentComponentData={{
                  ...selectedComponent,
                  ...componentSelfData,
                  zone_details:
                    componentSelfData?.zone_details ||
                    selectedComponent?.zone_details ||
                    (Array.isArray(selectedComponent?.ee_details)
                      ? selectedComponent.ee_details.find(
                          (ee) => ee.ee_id === localLinkApiProps?.ee_id
                        )?.zone_details
                      : selectedComponent?.ee_details?.zone_details),
                }}
              />
            ),
          },
          {
            name: "Attributes",
            count: attributes?.length || 0,
            content: (
              <Box>
                <Box mb={1}>
                  <CustomSearchFilterV2
                    placeholder={"Search"}
                    searchKeys={["attribute_heading", "attribute_value"]}
                    getDataOnSearch={(filteredData) => {
                      setFilteredAttributes(filteredData);
                      setPage(1);
                    }}
                    inputData={attributes}
                  />
                </Box>
                <Box
                  sx={{
                    width: inline ? "100%" : "750px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Box sx={{ height: "250px", overflow: "auto" }}>
                    {filteredAttributes && filteredAttributes.length > 0 ? (
                      filteredAttributes
                        .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                        .map((attr) => (
                          <Box
                            key={attr.id}
                            sx={{
                              padding: "10px",
                              border: "1px solid #e0e0e0",
                              textAlign: "left",
                            }}
                          >
                            <Box
                              sx={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: "#333",
                                marginBottom: "4px",
                              }}
                            >
                              {attr.attribute_heading}
                            </Box>
                            <Box
                              sx={{
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              {attr.attribute_type === "FILE" ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={!attr.attribute_value}
                                  onClick={() => {
                                    setSelectedFile(attr.attribute_value);
                                    setSelectedFileName(attr.attribute_heading);
                                    setOpenPreview(true);
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    p: 0,
                                    minWidth: 0,
                                    justifyContent: "flex-start",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  View
                                </Button>
                              ) : (
                                attr.attribute_value
                              )}
                            </Box>
                          </Box>
                        ))
                    ) : (
                      <Box
                        sx={{
                          padding: "16px",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        No attributes available
                      </Box>
                    )}
                  </Box>

                  {filteredAttributes && filteredAttributes.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <CustomPaginationV2
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(pageNo) => {
                          setPage(pageNo);
                        }}
                        totalItems={filteredAttributes.length}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            ),
          },
        ]}
      />
    </Box>
  );

  if (inline) {
    return (
      <>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {renderContent()}
        </Box>
        <ViewUploadedFile
          open={openPreview}
          onClose={() => {
            setOpenPreview(false);
            setSelectedFile(null);
          }}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          downloadFile={true}
          fileName={selectedFileName}
        />
      </>
    );
  }

  return (
    <>
      <CustomDialogBoxV2
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              pr: 4,
            }}
          >
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: "bold",
                color: "#67696d",
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                {type === "VS" || type === "VC" ? "Name:" : "Assembly No:"}{" "}
                <CustomToolTip length={80}>
                  {getComponentApiByType()?.headder?.name}
                </CustomToolTip>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {getVersionLabel()}
                <CustomToolTip length={80}>
                  {getComponentApiByType()?.headder?.version}
                </CustomToolTip>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                Description:{" "}
                <CustomToolTip length={80}>
                  {getComponentApiByType()?.headder?.description}
                </CustomToolTip>
              </Box>
              {showSupplier && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  Supplier Name:{" "}
                  <CustomToolTip length={80}>
                    {getComponentApiByType()?.headder?.supplier}
                  </CustomToolTip>
                </Box>
              )}
            </Box>
            {type === "EE" &&
              Array.isArray(selectedComponent?.ee_details) &&
              selectedComponent.ee_details.length > 1 && (
                <Box sx={{ minWidth: 200, mr: 2, ml: "auto" }}>
                  <Select
                    size="small"
                    value={localLinkApiProps?.ee_id || ""}
                    onChange={(e) =>
                      setLocalLinkApiProps({
                        ...localLinkApiProps,
                        ee_id: e.target.value,
                      })
                    }
                    displayEmpty
                    sx={{
                      width: "100%",
                      fontSize: "14px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedComponent.ee_details.map((ee) => (
                      <MenuItem
                        key={ee.ee_id}
                        value={ee.ee_id}
                        sx={{ fontSize: "14px" }}
                      >
                        {ee.description || `EE Component ${ee.ee_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            {type === "SWM" &&
              Array.isArray(selectedComponent?.swm_details) &&
              selectedComponent.swm_details.length > 1 && (
                <Box sx={{ minWidth: 200, mr: 2, ml: "auto" }}>
                  <Select
                    size="small"
                    value={localLinkApiProps?.swm_id || ""}
                    onChange={(e) =>
                      setLocalLinkApiProps({
                        ...localLinkApiProps,
                        swm_id: e.target.value,
                      })
                    }
                    displayEmpty
                    sx={{
                      width: "100%",
                      fontSize: "14px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedComponent.swm_details.map((swm) => (
                      <MenuItem
                        key={swm.swm_id}
                        value={swm.swm_id}
                        sx={{ fontSize: "14px" }}
                      >
                        {swm.name || `SWM Component ${swm.swm_id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            {type === "VS" &&
              Array.isArray(selectedComponent?.vehicle_system_details) &&
              selectedComponent.vehicle_system_details.length > 1 && (
                <Box sx={{ minWidth: 200, mr: 2, ml: "auto" }}>
                  <Select
                    size="small"
                    value={localLinkApiProps?.vs_id || ""}
                    onChange={(e) =>
                      setLocalLinkApiProps({
                        ...localLinkApiProps,
                        vs_id: e.target.value,
                      })
                    }
                    displayEmpty
                    sx={{
                      width: "100%",
                      fontSize: "14px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedComponent.vehicle_system_details.map((vs) => (
                      <MenuItem
                        key={vs.id}
                        value={vs.id}
                        sx={{ fontSize: "14px" }}
                      >
                        {vs.name || `Vehicle System ${vs.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            {type === "VC" &&
              Array.isArray(selectedComponent?.vehicle_component_details) &&
              selectedComponent.vehicle_component_details.length > 1 && (
                <Box sx={{ minWidth: 200, mr: 2, ml: "auto" }}>
                  <Select
                    size="small"
                    value={localLinkApiProps?.vc_id || ""}
                    onChange={(e) => {
                      const selectedVc =
                        selectedComponent.vehicle_component_details.find(
                          (vc) => vc.id === e.target.value
                        );
                      setLocalLinkApiProps({
                        ...localLinkApiProps,
                        vc_id: e.target.value,
                        vs_id:
                          selectedVc?.pr_vs_id || localLinkApiProps.vs_id,
                      });
                    }}
                    displayEmpty
                    sx={{
                      width: "100%",
                      fontSize: "14px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedComponent.vehicle_component_details.map(
                      (vc) => (
                        <MenuItem
                          key={vc.id}
                          value={vc.id}
                          sx={{ fontSize: "14px" }}
                        >
                          {vc.name || `Vehicle Component ${vc.id}`}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </Box>
              )}
          </Box>
        }
        open={isEEDialogOpen}
        onClose={() => {
          if (typeof toggleEEDialog === "function") {
            const result = toggleEEDialog(false);
            if (typeof result === "function") {
              result();
            }
          }
        }}
      >
        {renderContent()}
      </CustomDialogBoxV2>
      <ViewUploadedFile
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setSelectedFile(null);
        }}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        downloadFile={true}
        fileName={selectedFileName}
      />
    </>
  );
}

ModuleViewMoreDialog.propTypes = {
  isEEDialogOpen: PropTypes.bool,
  toggleEEDialog: PropTypes.func,
  selectedComponent: PropTypes.shape({
    supplier_name: PropTypes.string,
    zone_details: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.string,
    ]),
    ee_details: PropTypes.arrayOf(
      PropTypes.shape({
        ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        description: PropTypes.string,
        linked_ee_ids: PropTypes.array,
        zone_details: PropTypes.oneOfType([
          PropTypes.array,
          PropTypes.object,
          PropTypes.string,
        ]),
      })
    ),
    swm_details: PropTypes.arrayOf(
      PropTypes.shape({
        swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        linked_swm_ids: PropTypes.array,
      })
    ),
    vehicle_system_details: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      })
    ),
    vehicle_component_details: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        pr_vs_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }),
  linkApiProps: PropTypes.object,
  apiAction: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  apiDataKey: PropTypes.string,
  type: PropTypes.string,
  inline: PropTypes.bool,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showSupplier: PropTypes.bool,
};

export default ModuleViewMoreDialog;
